/**
 * Web Pagination Component
 */

import { memo, useContext, useRef } from 'react'
import type {
  ComponentType,
  HTMLAttributes,
  HTMLProps,
  JSX,
  ReactNode,
  Ref,
  RefObject,
  SyntheticEvent,
} from 'react'
import { clsx } from 'clsx'
import PaginationContext from './PaginationContext'
import PaginationProvider from './PaginationProvider'
import {
  validateDOMAttributes,
  extendExistingPropsWithContext,
  removeUndefinedProps,
} from '../../shared/component-helper'
import { useSpacing } from '../space/SpacingUtils'

import { PaginationIndicator } from './PaginationHelpers'
import InfinityScroller from './PaginationInfinity'
import PaginationBar from './PaginationBar'
import type { NavigationItemProps } from './PaginationBar'

import type { SkeletonShow } from '../Skeleton'
import type { SpacingProps, SpaceTypeAll } from '../../shared/types'
import type { ButtonIconPosition } from '../Button'
import withComponentMarkers from '../../shared/helpers/withComponentMarkers'
import type { ComponentMarkers } from '../../shared/helpers/withComponentMarkers'

export type PaginationStartupPage = string | number
export type PaginationCurrentPage = string | number
export type PaginationPageCount = string | number
export type PaginationStartupCount = string | number
export type PaginationParallelLoadCount = string | number
export type PaginationMinWaitTime = string | number
export type PaginationMode = 'pagination' | 'infinity'
export type PaginationLayout = 'vertical' | 'horizontal'
export type PaginationItems = string | unknown[]
export type PaginationSetContentHandler =
  | string
  | ((fn: (pageNumber: number, content: ReactNode) => void) => void)
export type PaginationResetContentHandler =
  | string
  | ((fn: () => void) => void)
export type PaginationResetPaginationHandler =
  | string
  | ((fn: () => void) => void)
export type PaginationEndInfinityHandler =
  | string
  | ((fn: () => void) => void)
export type PaginationPageElement =
  | Record<string, unknown>
  | ReactNode
  | ComponentType
  | string
export type PaginationFallbackElement =
  | Record<string, unknown>
  | ReactNode
  | ComponentType
  | string
export type PaginationMarkerElement =
  | Record<string, unknown>
  | ReactNode
  | ComponentType
  | string
export type PaginationIndicatorElement = ReactNode | ComponentType | string
export type PaginationChildrenArgs = {
  pageNumber: number
  setContent: (...args: unknown[]) => void
  endInfinity: () => void
  [key: string]: unknown
}
export type PaginationChildren =
  | ReactNode
  | ((props: PaginationChildrenArgs) => unknown)
export type PaginationLoadButtonProps =
  | (() => ReactNode)
  | {
      /**
       * Used during infinity mode. If `useLoadButton` is set to true, then a button is show on the bottom. If the `startupPage` is higher than 1. Defaults to `Vis mer innhold`.
       */
      text: string
      /**
       * Used during infinity mode. Sets the icon position on the `useLoadButton`. Default: `left`.
       */
      iconPosition: ButtonIconPosition
    }

export type PaginationEvent = {
  pageNumber: number
  setContent: (...args: unknown[]) => void
  endInfinity: () => void
  event?: SyntheticEvent
  [key: string]: unknown
}

export type PaginationProps = {
  /**
   * The page shown in the very beginning. If `currentPage` is set, then it may not make too much sense to set this as well.
   */
  startupPage?: PaginationStartupPage
  /**
   * The page shown at the moment the component renders. Defaults to `1`.
   */
  currentPage?: PaginationCurrentPage
  /**
   * The total pages count. Defaults to `1`.
   */
  pageCount?: PaginationPageCount
  /**
   * Defines how many `infinity` pages should be loaded / shown on the first render. Defaults to `1`.
   */
  startupCount?: PaginationStartupCount
  /**
   * Defines how many `infinity` pages should be loaded / shown once the user scrolls down. Defaults to `1`.
   */
  parallelLoadCount?: PaginationParallelLoadCount
  /**
   * If set to `true`, the infinity marker will be placed before the content (on top of). This could potentially have negative side effects. But it depends really on the content if this would make more sense to use instead. Defaults to `false`.
   */
  placeMarkerBeforeContent?: boolean
  /**
   * The minimum time to wait, if the infinity scroll was invoked under that time threshold. This prevents not intentional infinity scroll loop calls. Defaults to `400` milliseconds.
   */
  minWaitTime?: PaginationMinWaitTime
  /**
   * If set to `true`, all pagination bar buttons are disabled.
   */
  disabled?: boolean
  /**
   * If set to `true`, an overlaying skeleton with animation will be shown.
   */
  skeleton?: SkeletonShow
  /**
   * If set to `infinity`, then the pagination bar will not be shown, but infinity scrolling will do the content presentation. For more information, check out the [Infinity Scroller](/uilib/components/pagination/infinity-scroller). Defaults to `pagination`.
   */
  mode?: PaginationMode
  /**
   * The layout of the pagination bar. Defaults to `vertical`.
   */
  paginationBarLayout?: PaginationLayout
  /**
   * If set to `true`, it will disable the automated infinity scrolling, but shows a load more button at the end of the content instead.
   */
  useLoadButton?: boolean
  items?: PaginationItems
  /**
   * If set to `true`, no indicator will be shown.
   */
  hideProgressIndicator?: boolean
  /**
   * Callback function to get the `setContent` handler from the current pagination instance, e.g. `setContentHandler={fn => (...)}`. Use this handler to insert content during infinity mode.
   */
  setContentHandler?: PaginationSetContentHandler
  /**
   * Callback function to get the `resetContent` handler from the current pagination instance, e.g. `resetContentHandler={fn => (...)}`. Use this handler to reset all the content. You can set it to `true`, to programmatically reset the content.
   */
  resetContentHandler?: PaginationResetContentHandler
  /**
   * Callback function to get the `resetInfinity` handler from the current pagination instance, e.g. `resetPaginationHandler={fn => (...)}`. Use this handler to reset all the internal states. You can set it to `true`, to programmatically reset the states.
   */
  resetPaginationHandler?: PaginationResetPaginationHandler
  /**
   * Callback function to get the `endInfinity` handler from the current pagination instance, e.g. `endInfinityHandler={fn => (...)}`. Use this handler to end the infinity scrolling procedure, in case the `pageCount` is unknown.
   */
  endInfinityHandler?: PaginationEndInfinityHandler
  /**
   * By default a `<div>` is used. Set it to any element you have to use. Adds also a class: `dnb-pagination__page` shown.
   */
  pageElement?: PaginationPageElement
  /**
   * (infinity mode) is used by the _indicator_, _load more_ bar as well as by the marker. Defaults to a `div`.
   */
  fallbackElement?: PaginationFallbackElement
  /**
   * (infinity mode) is used by the internal marker. Falls back to `fallbackElement` if not defined.
   */
  markerElement?: PaginationMarkerElement
  /**
   * (infinity mode) is used by the _indicator_. Falls back to `fallbackElement` if not defined.
   */
  indicatorElement?: PaginationIndicatorElement
  /**
   * Define the alignment of the pagination button bar. Can be `center`, `left` or `right`. Defaults to `left`.
   */
  align?: string
  /**
   * The title used in every button shown in the bar. Defaults to `Side %s`.
   */
  buttonTitle?: string
  /**
   * The title used in the previous page button. Defaults to `Forrige side`.
   */
  prevTitle?: string
  /**
   * The title used in the next page button. Defaults to `Neste side`.
   */
  nextTitle?: string
  /**
   * The title used in the dots. Relevant for screen readers. Defaults to `%s flere sider`.
   */
  morePages?: string
  /**
   * Shown until new content is inserted into the page. Defaults to `Laster nytt innhold`.
   */
  isLoadingText?: string
  /**
   * Used to set load button text and icon alignment. Accepts a function returning a ReactNode too, so you can replace the button with your own component.
   */
  loadButton?: PaginationLoadButtonProps
  /**
   * Used to set spacing for the pagination bar. Has to be an object with either: `top`, `right`, `bottom` or `left`. Use spacing values like: `small`, `1rem`, `1` or `16px`. See property [space](/uilib/layout/space/properties).
   */
  barSpace?: SpaceTypeAll
  /**
   * A function that receives `(pageNumber, props)` and returns a React element. Spread the `props` onto your element to get the correct `NavigationItemProps`. Anchor-like elements keep the browser default behavior on modified or middle clicks.
   */
  transformNavigationItem?: (
    pageNumber: number,
    navigationItemProps: NavigationItemProps
  ) => ReactNode
  className?: string
  /**
   * The given content can be either a function or a React node, depending on your needs. A function contains several helper functions. More details down below and have a look at the examples in the demos section.
   */
  children?: PaginationChildren
  onChange?: (event: PaginationEvent) => void
  onStartup?: (event: PaginationEvent) => void
  onLoad?: (event: PaginationEvent) => void
  onEnd?: (event: PaginationEvent) => void
} & Omit<
  HTMLProps<HTMLElement>,
  'ref' | 'children' | 'onChange' | 'onLoad'
> &
  SpacingProps

export type PaginationCreateReturn = {
  Pagination: (props?: Record<string, unknown>) => JSX.Element
  InfinityMarker: (props?: Record<string, unknown>) => JSX.Element
  setContent: (pageNumber: number, content: ReactNode) => void
  resetContent: () => void
  resetInfinity: () => void
  endInfinity: () => void
}

const paginationDefaultProps: Partial<PaginationProps> = {
  mode: 'pagination',
  paginationBarLayout: 'vertical',
  useLoadButton: false,
  hideProgressIndicator: false,
  pageElement: undefined,
  fallbackElement: undefined,
  markerElement: undefined,
  indicatorElement: undefined,
  align: 'left',
  startupCount: 1,
  parallelLoadCount: 1,
  placeMarkerBeforeContent: false,
  minWaitTime: 400,
}

export type PaginationComponent = ((
  props: PaginationProps
) => JSX.Element) & {
  Bar: typeof PaginationBar
  Content: typeof PaginationContent
} & ComponentMarkers

function PaginationFunc(props: PaginationProps) {
  const mergedProps = {
    ...paginationDefaultProps,
    ...props,
  } as PaginationProps

  return (
    <PaginationProvider
      tagName="dnb-pagination"
      internalContent={mergedProps.children}
      {...mergedProps}
    >
      <PaginationInstance {...mergedProps} />
    </PaginationProvider>
  )
}

const Pagination = PaginationFunc as PaginationComponent

export default Pagination

const PaginationInstance = memo(function PaginationInstance(
  ownProps: PaginationProps
) {
  const ctx = useContext(PaginationContext)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const props = extendExistingPropsWithContext(
    ownProps,
    paginationDefaultProps,
    ctx.getTranslation(ownProps).Pagination,
    ctx.Pagination
  )

  const {
    align: _align,
    children: _children,
    className: _className,
    barSpace: _barSpace,
    paginationBarLayout: _paginationBarLayout,

    disabled: _disabled,
    skeleton: _skeleton,
    tagName: _tagName,
    pageCount: _pageCount,
    currentPage: _currentPageProp,
    startupPage: _startupPage,
    mode: _mode,
    hideProgressIndicator: _hideProgressIndicator,
    useLoadButton: _useLoadButton,
    currentPageInternal: _currentPage,
    markerElement: _markerElement,
    fallbackElement: _fallbackElement,
    setContentHandler: _setContentHandler,
    resetContentHandler: _resetContentHandler,
    resetPaginationHandler: _resetPaginationHandler,
    endInfinityHandler: _endInfinityHandler,
    minWaitTime: _minWaitTime,
    pageElement: _pageElement,
    startupCount: _startupCount,
    parallelLoadCount: _parallelLoadCount,
    buttonTitle: _buttonTitle,
    prevTitle: _prevTitle,
    nextTitle: _nextTitle,
    morePages: _morePages,
    isLoadingText: _isLoadingText,
    loadButton: _loadButton,
    indicatorElement: _indicatorElement,
    placeMarkerBeforeContent: _placeMarkerBeforeContent,
    transformNavigationItem: _transformNavigationItem,

    ...attributes
  } = props as Record<string, unknown>

  const { align, children, className, barSpace, paginationBarLayout } =
    props

  // our props
  const { currentPageInternal, items, fallbackElement, indicatorElement } =
    ctx.pagination

  const mainParams = useSpacing(props, {
    className: clsx(
      'dnb-pagination',
      align && `dnb-pagination--${align}`,
      paginationBarLayout &&
        `dnb-pagination--layout-${paginationBarLayout}`,
      className
    ),
    ...attributes,
  })

  // Pagination mode
  if (ctx.pagination.mode === 'pagination') {
    validateDOMAttributes(props, mainParams)

    const content = items.find(
      ({ pageNumber }) => pageNumber === currentPageInternal
    )?.content

    return (
      <div {...mainParams}>
        <PaginationBar contentRef={contentRef} space={barSpace}>
          {children as ReactNode}
        </PaginationBar>
        {items.length > 0 && (
          <PaginationContent ref={contentRef}>
            {content || (
              <PaginationIndicator
                indicatorElement={indicatorElement || fallbackElement}
              />
            )}
          </PaginationContent>
        )}
      </div>
    )
  }

  // InfinityScroller mode
  return <InfinityScroller />
})

export function InfinityMarker(props: PaginationProps) {
  const { children, ...rest } = {
    ...paginationDefaultProps,
    // Strip undefined values so they fall through to defaults,
    // preserving the legacy React defaultProps behavior.
    ...removeUndefinedProps({ ...props }),
  }

  return (
    <PaginationProvider
      useMarkerOnly
      tagName="dnb-infinity-marker"
      {...rest}
    >
      <InfinityScroller {...rest}>{children}</InfinityScroller>
    </PaginationProvider>
  )
}

function PaginationContent({
  children,
  ref,
  ...props
}: {
  children?: ReactNode
  ref?: Ref<HTMLDivElement>
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className="dnb-pagination__content dnb-no-focus"
      tabIndex={-1}
      {...props}
      ref={ref}
    >
      {children}
    </div>
  )
}

// NB: This is not ready yet
Pagination.Bar = PaginationBar
Pagination.Content = PaginationContent

const PaginationWrapper = PaginationFunc
const InfinityMarkerWrapper = InfinityMarker

export const Bar = (props: PaginationProps) => (
  <Pagination fallbackElement={() => null} {...props} />
)

export const createPagination = (
  initProps: Record<string, unknown> = {}
): PaginationCreateReturn => {
  const store: RefObject<Record<string, unknown> | null> = {
    current: null,
  }
  const rerender: RefObject<
    ((store: RefObject<Record<string, unknown> | null>) => void) | null
  > = { current: null }
  const _setContent: RefObject<
    ((pageNumber: number, content: ReactNode) => void) | null
  > = { current: null }
  const _resetContent: RefObject<(() => void) | null> = {
    current: null,
  }
  const _resetInfinity: RefObject<(() => void) | null> = {
    current: null,
  }
  const _endInfinity: RefObject<(() => void) | null> = {
    current: null,
  }

  const setContent = (pageNumber: number, content: ReactNode) => {
    if (pageNumber > 0) {
      store.current = { ...store.current, ...{ pageNumber, content } }
      rerender.current && rerender.current(store)
    }
  }
  const resetContent = () => {
    _resetContent.current && _resetContent.current()
  }
  const resetInfinity = () => {
    _resetInfinity.current && _resetInfinity.current()
  }
  const endInfinity = () => {
    _endInfinity.current && _endInfinity.current()
  }

  const args = (props: Record<string, unknown>) => ({
    ...{ ...initProps, ...props },
    store,
    rerender,
    setContentHandler: (fn: typeof _setContent.current) =>
      (_setContent.current = fn),
    resetContentHandler: (fn: typeof _resetContent.current) =>
      (_resetContent.current = fn),
    resetPaginationHandler: (fn: typeof _resetInfinity.current) =>
      (_resetInfinity.current = fn),
    endInfinityHandler: (fn: typeof _endInfinity.current) =>
      (_endInfinity.current = fn),
  })

  const Pagination = (props: Record<string, unknown>) => (
    <PaginationWrapper {...(args(props) as PaginationProps)} />
  )

  const InfinityMarker = (props: Record<string, unknown>) => (
    <InfinityMarkerWrapper {...(args(props) as PaginationProps)} />
  )

  return {
    Pagination,
    InfinityMarker,
    setContent,
    resetContent,
    resetInfinity,
    endInfinity,
  }
}
withComponentMarkers(Pagination, { _supportsSpacingProps: true })
